import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of, Subject, Subscriber, tap } from 'rxjs';
import { InitializationService } from './initialization.service';
import { SignOutService } from './sign-out.service';

@Injectable({
  providedIn: 'root',
})
export class RefreshTokenService {
  private initializationService = inject(InitializationService);
  private signOutService = inject(SignOutService);

  callingRefresh = false;
  private refreshTokenSubject = new Subject<void>();

  getRefreshTokenCallObservable(): Observable<void> {
    return this.refreshTokenSubject.asObservable();
  }

  refreshToken(refreshToken: string, navigateOnFailure = true): Observable<void> {
    this.callingRefresh = true;
    return new Observable(observer => {
      const subscription = this.initializationService
        .refreshToken(refreshToken)
        .pipe(
          tap(response => {
            if (response === null) {
              // if the token is expired or not found, the API will return null
              this.handleRefreshFailure(observer, navigateOnFailure);
            } else {
              // Save new token in local storage
              this.initializationService.saveTokens(response.token, response.refreshToken);
              this.callingRefresh = false;
              // Emit refresh token subject to proceed with requests that are waiting for the refresh to complete
              this.refreshTokenSubject.next();
              observer.next();
              observer.complete();
              subscription.unsubscribe();
            }
          }),
          catchError(() => {
            this.handleRefreshFailure(observer, navigateOnFailure);
            return of(null);
          })
        )
        .subscribe();
    });
  }

  private handleRefreshFailure(observer: Subscriber<void>, navigateOnFailure: boolean) {
    localStorage.removeItem('token');
    this.signOutService.signOut(navigateOnFailure);
    if (!navigateOnFailure) {
      this.callingRefresh = false;
      observer.error(new Error('Refresh token expired'));
    }
  }
}
